<template>
  <!-- "Approve my browser": apps like OpenClaw make each new browser wait for a
       one-time approval after it signed in. This lists the waiting browsers (it
       re-checks every few seconds while open) and approves them in one click. -->
  <v-dialog :value="value" max-width="560" @input="$emit('input', $event)">
    <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-pair-title">
      <gg-card-text class="pa-4">
        <h3 id="kvm-dk-pair-title" class="body-1 font-weight-medium mb-2">Approve your browser — {{ stack }}</h3>

        <v-alert v-if="approved" type="success" dense text class="mb-3" role="status">
          Approved. Go back to your {{ appName }} tab — it connects on its own.
        </v-alert>

        <p v-if="!requests.length" class="mb-3">
          Open {{ appName }} and sign in with your gateway token. When it says your browser needs approval,
          it shows up here — keep this window open.
        </p>

        <div v-if="loading && !loaded" class="py-4 text-center" role="status">
          <v-progress-circular indeterminate color="primary" size="24" width="2" />
          <span class="kvm-sr-only">Checking for waiting browsers…</span>
        </div>
        <div v-else-if="!requests.length" class="kvm-dk-wait kvm-help text-caption mb-3" role="status" aria-live="polite">
          <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" aria-hidden="true" />
          Waiting for a browser…
        </div>

        <div v-for="r in requests" :key="r.request_id" class="kvm-dk-req mb-2">
          <div class="d-flex flex-wrap align-center">
            <v-icon class="mr-2" aria-hidden="true">mdi-monitor-cellphone</v-icon>
            <div class="mr-auto">
              <div class="font-weight-medium">{{ r.client }}<template v-if="r.platform"> · {{ r.platform }}</template></div>
              <div class="kvm-help text-caption">
                <template v-if="r.remote_ip">From {{ r.remote_ip }}</template>
                <template v-if="r.requested_at"> · {{ ago(r.requested_at) }}</template>
              </div>
            </div>
            <template v-if="r.approvable">
              <gg-btn small text class="mr-1" :disabled="!!deciding" @click="decide(r, 'reject')">Decline</gg-btn>
              <gg-btn small tile color="primary" class="kvm-dk-primary" :loading="deciding === r.request_id" :disabled="!!deciding" @click="decide(r, 'approve')">
                Approve
              </gg-btn>
            </template>
            <template v-else>
              <gg-btn small text :disabled="!!deciding" @click="decide(r, 'reject')">Decline</gg-btn>
            </template>
          </div>
          <p v-if="!r.approvable" class="kvm-help text-caption mb-0 mt-1">
            This asks for more than a browser (it could control other machines), so it can only be approved on the server itself.
          </p>
        </div>

        <p v-if="requests.length" class="kvm-help text-caption mb-3">
          Only approve a browser you just signed in with. If you don't recognise it, decline it.
        </p>

        <v-alert v-if="error" type="warning" dense text class="mb-3" role="alert">{{ error }}</v-alert>

        <div class="text-right">
          <gg-btn text @click="$emit('input', false)">Close</gg-btn>
        </div>
      </gg-card-text>
    </gg-card>
  </v-dialog>
</template>

<script>
import kvm from '@/api/kvm'

// Each check starts the app's CLI on the server (~2s), so don't go faster.
const POLL_MS = 5000

export default {
  name: 'KvmDockerPairing',
  props: {
    value: { type: Boolean, default: false },
    serviceId: { type: [String, Number], required: true },
    stack: { type: String, default: '' },
    appName: { type: String, default: 'OpenClaw' },
  },
  data: () => ({
    requests: [],
    loading: false,
    loaded: false,
    error: '',
    deciding: null,
    approved: false,
    timer: null,
  }),
  watch: {
    value: {
      immediate: true,
      handler (open) {
        this.stop()
        if (open && this.stack) {
          this.requests = []
          this.loaded = false
          this.approved = false
          this.error = ''
          this.poll()
        }
      },
    },
  },
  methods: {
    async poll () {
      await this.refresh()
      if (this.value) this.timer = setTimeout(this.poll, POLL_MS)
    },
    async refresh () {
      if (this.deciding) return
      this.loading = true
      try {
        this.requests = await kvm.dockerPairingRequests(this.serviceId, this.stack)
        this.error = ''
      } catch (e) {
        // Right after install the app may still be starting (404/502).
        this.error = e.status === 404 || e.status === 502
          ? `${this.appName} isn't ready yet — this keeps checking.`
          : e.message
      } finally {
        this.loading = false
        this.loaded = true
      }
    },
    async decide (r, decision) {
      this.deciding = r.request_id
      this.error = ''
      try {
        await kvm.dockerDecidePairing(this.serviceId, this.stack, r.request_id, decision)
        this.requests = this.requests.filter(x => x.request_id !== r.request_id)
        if (decision === 'approve') this.approved = true
      } catch (e) {
        this.error = e.message
      } finally {
        this.deciding = null
      }
    },
    ago (iso) {
      const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
      if (s < 60) return 'just now'
      if (s < 3600) return `${Math.round(s / 60)} min ago`
      return new Date(iso).toLocaleString()
    },
    stop () {
      if (this.timer) clearTimeout(this.timer)
      this.timer = null
    },
  },
  beforeDestroy () {
    this.stop()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-wait { display: flex; align-items: center; }
.kvm-dk-req {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 10px;
}
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
